import {
  Box,
  Button,
  DialogActionTrigger,
  DialogTitle,
  HStack,
  Input,
  Stack,
  Text,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { useMemo, useState } from "react"
import { FiGitMerge } from "react-icons/fi"

import { ContentService, type SubjectPublic } from "@/client"
import type { ApiError } from "@/client/core/ApiError"
import useCustomToast from "@/hooks/useCustomToast"
import { useSubjectMerge } from "@/hooks/useSubjectMerge"
import { handleError } from "@/utils"
import { Checkbox } from "../ui/checkbox"
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTrigger,
} from "../ui/dialog"
import { Radio, RadioGroup } from "../ui/radio"

const MergeSubjects = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState("")
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [destinationId, setDestinationId] = useState<string | null>(null)
  const { showSuccessToast } = useCustomToast()
  const mergeMutation = useSubjectMerge()

  // Load every subject so the admin can consolidate the full list in one place.
  const { data, isLoading } = useQuery({
    queryKey: ["subjects", "all-for-merge"],
    queryFn: () => ContentService.listSubjects({ skip: 0, limit: 1000 }),
    enabled: isOpen,
  })

  const subjects = useMemo<SubjectPublic[]>(() => {
    const all = data?.data ?? []
    return [...all].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
    )
  }, [data])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return subjects
    return subjects.filter((s) => s.name.toLowerCase().includes(q))
  }, [subjects, search])

  const selectedSubjects = useMemo(
    () => subjects.filter((s) => selectedIds.has(s.id)),
    [subjects, selectedIds],
  )

  const reset = () => {
    setSearch("")
    setSelectedIds(new Set())
    setDestinationId(null)
  }

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
        if (destinationId === id) setDestinationId(null)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const destination = selectedSubjects.find((s) => s.id === destinationId)
  const sourceCount = selectedSubjects.length - (destination ? 1 : 0)
  const canMerge =
    !!destinationId && selectedSubjects.length >= 2 && !mergeMutation.isPending

  const onMerge = () => {
    if (!destinationId) return
    const sourceIds = selectedSubjects
      .filter((s) => s.id !== destinationId)
      .map((s) => s.id)
    if (sourceIds.length === 0) return

    mergeMutation.mutate(
      {
        destination_subject_id: destinationId,
        source_subject_ids: sourceIds,
      },
      {
        onSuccess: () => {
          showSuccessToast(
            `Merged ${sourceIds.length} subject${
              sourceIds.length === 1 ? "" : "s"
            } into "${destination?.name}".`,
          )
          reset()
          setIsOpen(false)
        },
        onError: (err) => handleError(err as ApiError),
      },
    )
  }

  return (
    <DialogRoot
      size={{ base: "xs", md: "lg" }}
      placement="center"
      open={isOpen}
      onOpenChange={({ open }) => {
        setIsOpen(open)
        if (!open) reset()
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" value="merge-subjects">
          <FiGitMerge />
          Merge Subjects
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Merge Subjects</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Text mb={3} color="fg.muted" fontSize="sm">
            Select the subjects to combine, then choose which one to keep. All
            questions, per-subject scores, and flashcard decks move to the kept
            subject and the others are deleted. This cannot be undone.
          </Text>

          <Input
            placeholder="Search subjects"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            mb={3}
            data-testid="merge-subject-search"
          />

          <Box
            maxH="260px"
            overflowY="auto"
            borderWidth="1px"
            borderRadius="md"
            p={2}
          >
            {isLoading ? (
              <Text color="fg.muted" p={2}>
                Loading subjects…
              </Text>
            ) : filtered.length === 0 ? (
              <Text color="fg.muted" p={2}>
                No subjects match your search.
              </Text>
            ) : (
              <Stack gap={1}>
                {filtered.map((subject) => (
                  <Box key={subject.id} px={2} py={1} borderRadius="sm">
                    <Checkbox
                      checked={selectedIds.has(subject.id)}
                      onCheckedChange={() => toggle(subject.id)}
                    >
                      {subject.name}
                    </Checkbox>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>

          {selectedSubjects.length >= 2 ? (
            <Box mt={4}>
              <Text fontWeight="medium" mb={2}>
                Keep as ({sourceCount} will be merged in)
              </Text>
              <RadioGroup
                value={destinationId ?? ""}
                onValueChange={({ value }) => setDestinationId(value)}
              >
                <Stack gap={1}>
                  {selectedSubjects.map((subject) => (
                    <Radio key={subject.id} value={subject.id}>
                      {subject.name}
                    </Radio>
                  ))}
                </Stack>
              </RadioGroup>
            </Box>
          ) : (
            <Text mt={4} color="fg.muted" fontSize="sm">
              Select at least two subjects to merge.
            </Text>
          )}
        </DialogBody>

        <DialogFooter gap={2}>
          <DialogActionTrigger asChild>
            <Button
              variant="subtle"
              colorPalette="gray"
              disabled={mergeMutation.isPending}
            >
              Cancel
            </Button>
          </DialogActionTrigger>
          <Button
            variant="solid"
            colorPalette="red"
            onClick={onMerge}
            disabled={!canMerge}
            loading={mergeMutation.isPending}
          >
            <HStack gap={1}>
              <FiGitMerge />
              <span>
                {destination
                  ? `Merge into "${destination.name}"`
                  : "Merge subjects"}
              </span>
            </HStack>
          </Button>
        </DialogFooter>
        <DialogCloseTrigger />
      </DialogContent>
    </DialogRoot>
  )
}

export default MergeSubjects
